import classNames from "classnames";
import { TextEntrySize } from "common/generator/components/text-entry-types";
import type { GlideFC } from "lib/glide-fc";
import * as React from "react";
import type { FormElementProps } from "../form-element/form-element";
import FormElement from "../form-element/form-element";
import GrowingEntryView from "../growing-entry/growing-entry-view";
import { TextFieldViewStyle } from "./text-field-view-style";
import type { FieldType } from "./text-field-view-types";

export interface TextFieldViewProps extends FormElementProps {
    readonly placeholder?: string;
    readonly value: string;
    readonly dataType?: FieldType;
    readonly focused?: boolean;
    readonly onChange?: (ev: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
    readonly onFocus?: () => void;
    readonly onBlur?: () => void;
    readonly maximumChars?: number;
    readonly size?: TextEntrySize;
    readonly prefix?: string;
    readonly suffix?: string;
}

type Props = TextFieldViewProps;

const SingleInput = (
    p: { inputRef: any } & React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>
) => <input ref={p.inputRef} {...p} />;

const TextFieldView: GlideFC<Props> = props => {
    const {
        dataType = "text",
        focused = false,
        isEnabled = true,
        onBlur,
        onFocus,
        onChange,
        placeholder,
        title,
        value,
        errorMessage,
        prefix,
        suffix,
        maximumChars,
        size = TextEntrySize.Normal,
    } = props;

    const inputRef = React.useRef<HTMLInputElement>(null);

    let hint: string | undefined;
    let charCountIsError = false;
    if (maximumChars !== undefined) {
        hint = `${value.length} / ${maximumChars}`;
        charCountIsError = value.length > maximumChars;
    }

    const Input = dataType === "text" ? GrowingEntryView : SingleInput;

    return (
        <FormElement {...props} hint={hint}>
            <TextFieldViewStyle
                isEnabled={isEnabled}
                className={classNames({
                    error: errorMessage !== undefined || charCountIsError,
                    focused: isEnabled && focused,
                })}
                data-test="app-text-field"
                onClick={ev => {
                    // FIXME current is null
                    inputRef.current?.focus();
                    onFocus?.();
                    ev.stopPropagation();
                }}>
                <div className="input-wrap">
                    {prefix && <div className="prefix">{prefix}</div>}
                    <Input
                        className={classNames("input", {
                            medium: size === TextEntrySize.Medium,
                            large: size === TextEntrySize.Large,
                        })}
                        inputRef={inputRef}
                        type={dataType}
                        data-test={`app-tf-${dataType}-input`}
                        aria-label={title}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        placeholder={placeholder}
                        value={value}
                        onChange={onChange}
                    />
                    {suffix && <div className="suffix">{suffix}</div>}
                </div>
            </TextFieldViewStyle>
        </FormElement>
    );
};

export default TextFieldView;
